import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, Space, message, Modal, Switch, Card, Tag, Divider } from 'antd';
import { SearchOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, EditOutlined, FilterOutlined, EyeOutlined } from '@ant-design/icons';
import './index.css';
import apiClient from '../../utils/apiService';

const { Option } = Select;

function RoleManage() {
  const [form] = Form.useForm();
  const [createRoleForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // 状态管理
  const [roleData, setRoleData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [roleUpdateForm] = Form.useForm();
  const [isRoleUpdateModalVisible, setIsRoleUpdateModalVisible] = useState(false);
  // 更新还是查看
  const [isUpdate, setIsUpdate] = useState(false); // false for view, true for update

  // 表格列定义
  const columns = [
    { 
      title: 'ID', 
      dataIndex: 'id', 
      key: 'id', 
      width: 80, 
      align: 'center', 
      fixed: 'left',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    { 
      title: 'Role Code', 
      dataIndex: 'roleCode', 
      key: 'roleCode', 
      width: 150, 
      align: 'center',
      render: (text) => <strong>{text}</strong>
    },
    { 
      title: 'Role Name', 
      dataIndex: 'roleName', 
      key: 'roleName', 
      width: 150, 
      align: 'center',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    { 
      title: 'Role Description', 
      dataIndex: 'roleDesc', 
      key: 'roleDesc', 
      width: 200, 
      render: (text) => text || '-' 
    },
    { 
      title: 'Data Scope', 
      dataIndex: 'dataScope', 
      key: 'dataScope', 
      width: 120, 
      render: (text) => {
        const colorMap = {
          'ALL': 'green',
          'SELF': 'orange',
          'DEPARTMENT': 'geekblue'
        };
        return <Tag color={colorMap[text] || 'default'}>{text}</Tag>;
      }
    },
    { 
      title: 'Edit Permission', 
      dataIndex: 'editPermission', 
      key: 'editPermission', 
      width: 120, 
      render: (text) => {
        const colorMap = {
          'EDIT_ALL': 'green',
          'EDIT_SELF': 'orange',
          'READ_ONLY': 'gray'
        };
        return <Tag color={colorMap[text] || 'default'}>{text}</Tag>;
      }
    },
    { 
      title: 'Can Edit Finance', 
      dataIndex: 'canEditFinance', 
      key: 'canEditFinance', 
      width: 100,
      render: (text) => text ? <Tag color="success">允许</Tag> : <Tag color="error">禁止</Tag>
    },
    { 
      title: 'Can Search All', 
      dataIndex: 'canSearchAll', 
      key: 'canSearchAll', 
      width: 100,
      render: (text) => text ? <Tag color="success">允许</Tag> : <Tag color="error">禁止</Tag>
    },
    { 
      title: 'Can Export', 
      dataIndex: 'canExport', 
      key: 'canExport', 
      width: 100,
      render: (text) => text ? <Tag color="success">允许</Tag> : <Tag color="error">禁止</Tag>
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status', 
      width: 100,
      render: (text) => (
        <Tag color={text === 1 ? 'success' : 'error'}>
          {text === 1 ? '启用' : '禁用'}
        </Tag>
      )
    },
    { 
      title: 'Creation Time',
      dataIndex: 'createdTime', 
      key: 'createdTime', 
      width: 160, 
      render: (text) => text || '-' 
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => { 
              setIsUpdate(false);
              showRoleUpdateModal(record); 
            }}
          >
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => { 
              setIsUpdate(true);
              showRoleUpdateModal(record); 
            }}
          >
          </Button>
          <Button
            type="primary"
            danger
            ghost
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => showDeleteModal(record)}
          >
          </Button>
        </Space>
      ),
      width: 220,
      fixed: 'right',
    },
  ];

  // 通用 API 调用函数
  const fetchData = async (url, method = 'get', data = {}) => {
    try {
      let response;
      if (method.toLowerCase() === 'get') {
        response = await apiClient.get(url, { params: data });
      } else {
        response = await apiClient[method](url, data);
      }
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      messageApi.error(errorMessage);
      throw error;
    }
  };

  // 获取角色数据
  const loadRoleData = async (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);
    try {
      const params = {
        pageNum: page,
        pageSize: pageSize,
        ...filters
      };

      const response = await fetchData('/user-management/role-permissions', 'post', params);
      const { data } = response;

      if (data && data.records) {
        setRoleData(data.records);
        setPagination({
          current: data.pageNum,
          pageSize: data.pageSize,
          total: data.total
        });
      } else {
        setRoleData([]);
        setPagination({ current: 1, pageSize: 10, total: 0 });
      }
    } catch (error) {
      setRoleData([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
      console.error('Failed to load role data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载数据
  useEffect(() => {
    loadRoleData();
  }, []);

  // 查询表单提交
  const handleSearch = (values) => {
    const filters = {};
    if (values.roleCode) filters.roleCode = values.roleCode;
    if (values.roleName) filters.roleName = values.roleName;
    if (values.status !== undefined && values.status !== null) filters.status = values.status;

    loadRoleData(1, pagination.pageSize, filters);
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    loadRoleData(1, pagination.pageSize);
  };

  // 显示删除确认弹窗
  const showDeleteModal = (role) => {
    setCurrentRole(role);
    setIsDeleteModalVisible(true);
  };

  // 显示创建角色弹窗
  const showCreateModal = () => {
    createRoleForm.resetFields();
    setIsCreateModalVisible(true);
  };

  // 创建角色
  const handleCreateRole = async (values) => {
    try {
      await fetchData('/user-management/create-role-permission', 'post', values);
      messageApi.success('role created successfully');
      setIsCreateModalVisible(false);
      loadRoleData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to create role:', error);
    }
  };

  // 显示角色更新弹窗
  const showRoleUpdateModal = (role) => {
    setCurrentRole(role);
    
    // 设置默认值
    roleUpdateForm.setFieldsValue({
      roleCode: role.roleCode,
      roleName: role.roleName,
      roleDesc: role.roleDesc,
      dataScope: role.dataScope,
      editPermission: role.editPermission,
      canEditFinance: role.canEditFinance === 1,
      canSearchAll: role.canSearchAll === 1,
      canExport: role.canExport === 1,
      status: role.status
    });

    setIsRoleUpdateModalVisible(true);
  };

  // 更新角色
  const handleUpdateRole = async (values) => {
    try {
      // 将布尔值转换为数字值
      const processedValues = {
        ...values,
        canEditFinance: values.canEditFinance ? 1 : 0,
        canSearchAll: values.canSearchAll ? 1 : 0,
        canExport: values.canExport ? 1 : 0,
      };
      
      const response = await fetchData(`/user-management/role-permissions/${currentRole.id}`, 'put', processedValues);
      if (response.code === 200) {
        messageApi.success('role updated successfully');
        setIsRoleUpdateModalVisible(false);
        loadRoleData(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  // 删除角色
  const handleDeleteRole = async () => {
    try {
      await fetchData(`/user-management/role-permissions/${currentRole.id}`, 'delete');
      messageApi.success('role deleted successfully');
      setIsDeleteModalVisible(false);
      loadRoleData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to delete role:', error);
    }
  };

  return (
    <div className="roleManage-container">
      {contextHolder}

      {/* 查询表单 */}
      <Card className="roleManage-search-card">
        <Form form={form} name="search" onFinish={handleSearch} layout="vertical">
          <div className="form-items">
            <Form.Item name="roleCode" label="Role Code" className="form-item">
              <Input placeholder="Enter Role Code" prefix={<FilterOutlined />} allowClear />
            </Form.Item>
            <Form.Item name="roleName" label="Role Name" className="form-item">
              <Input placeholder="Enter Role Name" prefix={<FilterOutlined />} allowClear />
            </Form.Item>
            <Form.Item name="status" label="Status" className="form-item">
              <Select 
                placeholder="Select Status" 
                allowClear
                options={[
                  { value: 1, label: '启用' },
                  { value: 0, label: '禁用' },
                ]}
              />
            </Form.Item>
          </div>
          <div className="form-buttons">
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                Search
              </Button>
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
              >
                Reset
              </Button>
            </Space>
          </div>
        </Form>
      </Card>

      {/* 数据表格 */}
      <div className="rolemanage-table">
        <Button 
          style={{ marginBottom: 16 }} 
          type="primary" 
          onClick={showCreateModal} 
          icon={<PlusOutlined />}
          size="middle"
        >
          Create Role
        </Button>

        <Table
          className='rolemanage-table-table'
          columns={columns}
          size='middle'
          dataSource={roleData.map((item) => ({ ...item, key: item.id }))}
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => {
              const formValues = form.getFieldsValue();
              const filters = {};
              if (formValues.roleCode) filters.roleCode = formValues.roleCode;
              if (formValues.roleName) filters.roleName = formValues.roleName;
              if (formValues.status !== undefined && formValues.status !== null) filters.status = formValues.status;

              loadRoleData(page, pageSize, filters);
            },
          }}
          loading={loading}
          scroll={{ x: 'max-content', y: 500 }}
          style={{ maxWidth: '100%' }}
        />
      </div>

      {/* 删除确认弹窗 */}
      <Modal
        title="Confirm Delete"
        open={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        onOk={handleDeleteRole}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete the role "<strong>{currentRole?.roleName}</strong>" (Code: {currentRole?.roleCode})?</p>
      </Modal>

      {/* 创建角色弹窗 */}
      <Modal
        title="Create New Role"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={
          <Space>
            <Button type="primary" onClick={() => createRoleForm.submit()}>
              Create Role
            </Button>
            <Button onClick={() => setIsCreateModalVisible(false)}>
              Cancel
            </Button>
          </Space>
        }
        width={700}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      >
        <Form 
          form={createRoleForm} 
          layout="vertical" 
          onFinish={(values) => {
            // 将布尔值转换为数字值
            const processedValues = {
              ...values,
              canEditFinance: values.canEditFinance ? 1 : 0,
              canSearchAll: values.canSearchAll ? 1 : 0,
              canExport: values.canExport ? 1 : 0,
            };
            handleCreateRole(processedValues);
          }}
        >
          <Form.Item
            label="Role Code"
            name="roleCode"
            rules={[{ required: true, message: 'Please enter role code' }]}
          >
            <Input placeholder="Enter role code, e.g.: ADMIN, MANAGER" />
          </Form.Item>
          
          <Form.Item
            label="Role Name"
            name="roleName"
            rules={[{ required: true, message: 'Please enter role name' }]}
          >
            <Input placeholder="Enter role name, e.g.: Administrator, Manager" />
          </Form.Item>
          
          <Form.Item
            label="Role Description"
            name="roleDesc"
          >
            <Input.TextArea rows={3} placeholder="Enter role description" />
          </Form.Item>
          
          <Form.Item
            label="Data Scope"
            name="dataScope"
            rules={[{ required: true, message: 'Please select data scope' }]}
          >
            <Select placeholder="Please select data scope">
              <Option value="ALL">All</Option>
              <Option value="SELF">Self</Option>
              <Option value="DEPARTMENT">Department</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="Edit Permission"
            name="editPermission"
            rules={[{ required: true, message: 'Please select edit permission' }]}
          >
            <Select placeholder="Select edit permission">
              <Option value="EDIT_ALL">Edit All</Option>
              <Option value="EDIT_SELF">Edit Self</Option>
              <Option value="READ_ONLY">Read Only</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="Finance Edit Permission"
            name="canEditFinance"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item>
          
          {/* <Form.Item
            label="All Search Permission"
            name="canSearchAll"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item> */}
          
          <Form.Item
            label="Export Permission"
            name="canExport"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item>
          
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
            initialValue={1}
          >
            <Select>
              <Option value={1}>On</Option>
              <Option value={0}>Off</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 更新角色弹窗 */}
      <Modal
        title={`Update Role Information - ${currentRole?.roleName}`}
        open={isRoleUpdateModalVisible}
        onCancel={() => setIsRoleUpdateModalVisible(false)}
        footer={isUpdate ? (
          <Space>
            <Button type="primary" onClick={() => roleUpdateForm.submit()}>
              Update Role
            </Button>
            <Button onClick={() => setIsRoleUpdateModalVisible(false)}>
              Cancel
            </Button>
          </Space>
        ) : null}
        width={700}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      >
        <Form 
          form={roleUpdateForm} 
          layout="vertical"
          onFinish={(values) => {
            // 将布尔值转换为数字值
            const processedValues = {
              ...values,
              canEditFinance: values.canEditFinance ? 1 : 0,
              canSearchAll: values.canSearchAll ? 1 : 0,
              canExport: values.canExport ? 1 : 0,
            };
            handleUpdateRole(processedValues);
          }}
        >
          <Form.Item
            label="Role Code"
            name="roleCode"
            rules={[{ required: true, message: 'Please enter role code' }]}
          >
            <Input placeholder="Enter role code" disabled />
          </Form.Item>
          
          <Form.Item
            label="Role Name"
            name="roleName"
            rules={[{ required: true, message: 'Please enter role name' }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>
          
          <Form.Item
            label="Role Description"
            name="roleDesc"
          >
            <Input.TextArea rows={3} placeholder="Enter role description" />
          </Form.Item>
          
          <Form.Item
            label="Data Scope"
            name="dataScope"
            rules={[{ required: true, message: 'Please select data scope' }]}
          >
            <Select placeholder="Select data scope">
              <Option value="ALL">All</Option>
              <Option value="SELF">Self</Option>
              <Option value="DEPARTMENT">Department</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="Edit Permission"
            name="editPermission"
            rules={[{ required: true, message: 'Please select edit permission' }]}
          >
            <Select placeholder="Select edit permission">
              <Option value="EDIT_ALL">Edit All</Option>
              <Option value="EDIT_SELF">Edit Self</Option>
              <Option value="READ_ONLY">Read Only</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="Finance Edit Permission"
            name="canEditFinance"
            valuePropName="checked"
          >
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item>
          
          {/* <Form.Item
            label="All Search Permission"
            name="canSearchAll"
            valuePropName="checked"
          >
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item> */}
          
          <Form.Item
            label="Export Permission"
            name="canExport"
            valuePropName="checked"
          >
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item>
          
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Option value={1}>On</Option>
              <Option value={0}>Off</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default RoleManage;